// noinspection DuplicatedCode

/*
 * Copyright (c) 2021. American Express Company
 */

// third-party
import _ from 'lodash';
import $ from 'jquery';
import { ChronoUnit, LocalDate } from '@js-joda/core';
import React, { Component, ReactComponentElement } from 'react';
import FormControlLabel from '@mui/material/FormControlLabel/FormControlLabel';

// models and services
import { CashFlowModel } from 'src/types/cash-flow.model';
import { JaegerContainerViewModel } from 'src/types/view-models/jaeger-container.view.model';
import CashFlowService from '../../services/cashflow.service';
import FormatService from '../../services/format.service';
import UnitService from '../../services/unit.service';

// components
import Anchor from 'src/components/atomic/atoms/anchor/Anchor';
import ClickableIcon from 'src/bw/clickable-icon/ClickableIcon';
import MiniDropdown from '../../components/menus/MiniDropdown';
import MoreInfoDialog from 'src/bw/more-info/MoreInfoDialog';
import ProjectJaeger from './ProjectJaeger';
import {
    ButtonSecondary,
    GlyphLinkOut,
    GlyphMinus,
    GlyphPlus,
    IconCheck,
    IconSearch,
    IconTrash,
    ToggleSwitch
} from '@americanexpress/dls-react';
import Typography from '../../components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';

// constants, icons and style
import { HELP_DOMAIN, TIMELINE_SCREEN_PORTION, ZOOM_SELECT_OFFSET } from 'src/utils/constants';
import styles from './JaegerContainer.module.scss';
import LiteralsService from '../../services/literals/LiteralsService';
import { FullUserModel } from 'src/types/user.model';
import { DLSSize } from 'src/types/dls-size.enum';

interface JaegerContainerState {
    topLegendOpen: boolean;
    topLegendX: number;
    topLegendY: number;
    bottomLegendOpen: boolean;
    bottomLegendX: number;
    bottomLegendY: number;
    showWorkType: string;
    showWorkCashFlow: CashFlowModel;
    showWorkOpen: boolean;
    showWorkX: number;
    showWorkY: number;
    showWorkValue: number;
    showWorkZoom: string;
    width: number;
    height: number;
    dangerOpen: boolean;
    dangerWarn: boolean;
    dangerDate: LocalDate;
    showEventNames: boolean;
}

export interface JaegerContext {
    width: number;
    height: number;
    offset: number;
    magnification: number;
    timelinePosition: number;
    minMagnification: number;
    minOffset: number;
}

const DANGER_WIDTH = 350;

export default class JaegerContainer extends Component<JaegerContainerViewModel, JaegerContainerState> {
    public resizeListener: EventListenerOrEventListenerObject;

    public override state = {
        openAvatarMenu: false,
        topLegendOpen: false,
        topLegendX: 0,
        topLegendY: 0,
        bottomLegendOpen: false,
        bottomLegendX: 0,
        bottomLegendY: 0,
        showWorkType: '',
        showWorkCashFlow: null as CashFlowModel,
        showWorkOpen: false,
        showWorkX: 0,
        showWorkY: 0,
        showWorkValue: 0,
        showWorkZoom: null as string,
        width: 0,
        height: 0,
        dangerOpen: false,
        dangerWarn: false,
        dangerDate: null as LocalDate,
        showEventNames: false
    };

    public override componentDidMount(): void {
        this.resetDimensions();
        this.resizeListener = () => {
            this.resetDimensions();
        };

        window.addEventListener('resize', this.resizeListener);
    }

    public override componentWillUnmount(): void {
        window.removeEventListener('resize', this.resizeListener);

        if (this.showWorkTimer) {
            clearTimeout(this.showWorkTimer);
        }
    }

    public override componentDidUpdate(prevProps: Readonly<JaegerContainerViewModel>): void {
        const fullDrawer = $('.bw-full-drawer');
        const jaegerContainer = $('.jaegerContainer');
        if (fullDrawer[0] && jaegerContainer.is(':visible')) {
            jaegerContainer.hide();
            this.resetDimensions(false);
        }

        if (!fullDrawer[0] && jaegerContainer.is(':hidden')) {
            jaegerContainer.show();
            this.resetDimensions();
            return;
        }

        if (prevProps.months !== this.props.user.rawCashFlow.length) {
            this.resetDimensions();
        }
    }

    private showWorkTimer!: number;
    private dangerOpenTimer!: number;

    public override render(): ReactComponentElement<any> {
        if (!this.props.context) return null;

        let ageLabel: string = '';
        let thisAge: number = 0;

        if (this.props.user?.dob && this.state.showWorkOpen) {
            thisAge = CashFlowService.getAgeAtCashFlow(this.props.user, this.state.showWorkCashFlow);
            if (this.state.showWorkZoom === 'months') {
                ageLabel = `Age ${thisAge}`;
            } else {
                ageLabel = `Age ${thisAge}-${thisAge + 1}`;
            }
        }
        const marginLeft = UnitService.getDrawerWidth();
        const helpLinkUrl: string = HELP_DOMAIN ? HELP_DOMAIN + '/' : null;

        let dangerTitle: string;
        let dangerBody: string;
        let dangerUrl: string;
        let dangerAnchor: string;
        let dangerX: number;

        if (this.state.dangerOpen) {
            if (this.state.dangerOpen) {
                dangerUrl = HELP_DOMAIN ? HELP_DOMAIN + '/articles/danger-bar' : null;
                dangerAnchor = 'Learn more about the Danger Bar';

                if (this.state.dangerWarn) {
                    dangerTitle = 'You’re dipping into your savings.';
                    dangerBody =
                        'When you see this orange warning, that means you need to dip into your savings to cover the' +
                        ' cost of your expenses and life events.';
                } else {
                    dangerTitle = 'You’re out of money.';
                    dangerBody =
                        'When you see this red warning, that means you’ve spent all your savings and need to sell' +
                        ' assets or property to be able to afford your plan.';
                }
            }

            if (this.state.dangerDate) {
                dangerX = UnitService.getJaegerX(this.props.context, this.props.user, this.state.dangerDate);
                dangerX = Math.min(this.state.width - DANGER_WIDTH, dangerX);
            }
        }

        const startupStep: number = _.get(this, 'props.user.attributes.startupStep');

        return (
            <div
                className={styles.jaegerContainer}
                style={{
                    width: `${this.state.width}px`,
                    marginLeft: `${marginLeft}px`
                }}
            >
                <ProjectJaeger
                    width={this.state.width}
                    height={this.state.height}
                    user={this.props.user}
                    onJaegerDrop={this.props.cardFactory.onJaegerDrop.bind(this.props.cardFactory)}
                    onJaegerMove={this.props.cardFactory.onJaegerMove.bind(this.props.cardFactory)}
                    onJaegerUpdate={this.props.cardFactory.onJaegerUpdate.bind(this.props.cardFactory)}
                    handleDeleteCard={this.props.cardFactory.handleDeleteCard.bind(this.props.cardFactory)}
                    history={this.props.history}
                    showWork={this.showWork.bind(this)}
                    endShowWork={this.endShowWork.bind(this)}
                    showLegendBox={this.showLegendBox.bind(this)}
                    hideLegendBox={this.hideLegendBox.bind(this)}
                    view={this.props.view}
                    handleOpenDanger={this.handleOpenDanger.bind(this)}
                    handleCloseDanger={this.handleCloseDanger.bind(this)}
                    context={this.props.context}
                    updateJaegerContext={this.props.updateJaegerContext}
                    showEventNames={this.state.showEventNames}
                    magnification={this.props.context.magnification}
                    showDangerBar={
                        this.props.user.attributes.hideDangerBar ? !this.props.user.attributes.hideDangerBar : true
                    }
                    incomeColor={startupStep === undefined || this.props.user.attributes.startupShowIncome}
                    expensesColor={startupStep === undefined || this.props.user.attributes.startupShowExpenses}
                    assetsColor={startupStep === undefined || this.props.user.attributes.startupShowAssets}
                    dragFirstGoal={this.props.user.attributes.startupDragGoal}
                    firstDrag={this.props.firstDrag}
                />
                <div id="eventContextMenu">
                    <div className={styles.contextSubHeading}>
                        <Typography variant={TypographyVariants.SUBTEXT_BOLD}>
                            {LiteralsService.getJsxLiteral('Timeline.eventDisplaySize')}
                        </Typography>
                    </div>
                    <div className={styles.eventContextItem} id="smallEventButton">
                        <IconCheck className={styles.checkIcon} />
                        <Typography variant={TypographyVariants.SUBTEXT}>Small</Typography>
                    </div>
                    <div className={styles.eventContextItem} id="defaultEventButton">
                        <IconCheck className={styles.checkIcon} />
                        <Typography variant={TypographyVariants.SUBTEXT}>Default</Typography>
                    </div>
                    <div className={styles.eventContextItem} id="largeEventButton">
                        <IconCheck className={styles.checkIcon} />
                        <Typography variant={TypographyVariants.SUBTEXT}>Large</Typography>
                    </div>
                    <div className={styles.eventContextDivider}></div>
                    <div className={styles.eventContextItem} id="deleteEventButton">
                        <IconTrash />
                        <Typography variant={TypographyVariants.SUBTEXT}>Delete</Typography>
                    </div>
                </div>
                {this.state.showWorkOpen ? (
                    <div
                        className={styles.showWorkDiv}
                        style={{
                            top: this.state.showWorkY,
                            left: this.state.showWorkX
                        }}
                    >
                        <Typography variant={TypographyVariants.CAPTION}>
                            {CashFlowService.getDateForCashFlow(
                                this.state.showWorkZoom === 'years' ? 'years' : 'months',
                                this.state.showWorkCashFlow
                            )}{' '}
                            • {ageLabel}
                        </Typography>
                        <br />
                        <Typography variant={TypographyVariants.LEGAL_PRIMARY}>
                            {CashFlowService.isCashFlowInFuture(this.state.showWorkCashFlow) ? 'Projected ' : ''}
                            <span className="type">{this.state.showWorkType}</span>:
                        </Typography>
                        <br />
                        <Typography variant={TypographyVariants.HEADING_M} className={styles.showWorkAmount}>
                            {FormatService.format(this.state.showWorkValue)}
                        </Typography>
                        <Typography variant={TypographyVariants.LEGAL_PRIMARY}>
                            Click bar for projection details.
                        </Typography>
                        <div className={styles.arrowBottom} />
                    </div>
                ) : null}
                {this.props.view && this.getLegendBox(this.props.view.split('-')[0])}
                {this.props.view?.length > 1 && this.getLegendBox(this.props.view.split('-')[1])}
                <div className={styles.combinedHeader}>
                    {
                        // startupStep can be 0, but not when the JaegerContainer is visible
                        /* this.props.user.attributes.startupStep ? null : <ScenarioMenu
                            user={this.props.user}
                            handleReloadUser={this.props.handleReloadUser} /> */
                    }
                    <FormControlLabel
                        control={
                            <ToggleSwitch
                                checked={this.state.showEventNames}
                                onClick={this.handleToggleShowEventNames.bind(this)}
                            />
                        }
                        label="Show event names"
                        labelPlacement="start"
                        className={this.state.showEventNames ? 'switch-control-on' : 'switch-control-off'}
                    />
                    {helpLinkUrl ? (
                        <ButtonSecondary
                            size={DLSSize.MEDIUM}
                            className={styles.helpDiv}
                            onClick={() => {
                                window.open(helpLinkUrl, '_blank');
                            }}
                        >
                            <div>
                                <IconSearch />
                            </div>
                            <div>Support &amp; FAQ</div>
                        </ButtonSecondary>
                    ) : null}
                    <div className={styles.plusMinus}>
                        <div className={styles.control}>
                            <ClickableIcon
                                icon={<GlyphPlus />}
                                onClick={() => {
                                    this.magnify(1);
                                }}
                            />
                        </div>
                        <div className={styles.plusMinusDivider}></div>
                        <div className={styles.control}>
                            <ClickableIcon
                                icon={<GlyphMinus />}
                                onClick={() => {
                                    this.magnify(0);
                                }}
                            />
                        </div>
                    </div>
                </div>
                {this.props.user.attributes.hideViewControls ? null : (
                    <React.Fragment>
                        <MiniDropdown
                            id="viewControlsDropdown"
                            value={this.props.view}
                            onChange={(e) => {
                                this.props.handleView(e.target.value);
                            }}
                            className={styles.viewControls}
                            style={{
                                top: `calc(${this.props.context.timelinePosition * 100}% + ${ZOOM_SELECT_OFFSET}px)`
                            }}
                        >
                            <option key="income-expense" value="income-expense">
                                Income & Expenses
                            </option>
                            <option key="investment-debt" value="investment-debt">
                                Assets & Debts
                            </option>
                            <option key="networth" value="networth">
                                Net Worth
                            </option>
                        </MiniDropdown>
                    </React.Fragment>
                )}
                {this.state.dangerOpen ? (
                    <MoreInfoDialog
                        open={this.state.dangerOpen}
                        title={dangerTitle}
                        body={dangerBody}
                        linkName={dangerAnchor}
                        link={dangerUrl}
                        anchor={null}
                        handleClose={this.handleCloseDanger.bind(this)}
                        handleOpen={this.handleOpenDanger.bind(this)}
                        x={dangerX}
                        y={this.state.height * this.props.context.timelinePosition - 200}
                    />
                ) : null}
            </div>
        );
    }

    showLegendBox(top: boolean, x: number, y: number): void {
        if (top) {
            this.setState({
                topLegendOpen: true,
                bottomLegendOpen: false,
                topLegendX: x - 151,
                topLegendY: y - 205,
                bottomLegendX: 0,
                bottomLegendY: 0
            });
        } else {
            this.setState({
                bottomLegendOpen: true,
                topLegendOpen: false,
                bottomLegendX: x - 151,
                bottomLegendY: y - 205,
                topLegendX: 0,
                topLegendY: 0
            });
        }
    }

    hideLegendBox(): void {
        this.setState({
            topLegendOpen: false,
            bottomLegendOpen: false,
            topLegendX: 0,
            topLegendY: 0,
            bottomLegendX: 0,
            bottomLegendY: 0
        });
    }

    private getLegendBox(type: string): JSX.Element {
        let titleText: string;
        let bodyText: JSX.Element;
        let left: number;
        let top: number;
        let display: boolean;
        const user: FullUserModel = this.props.user;
        const offset: number = CashFlowService.getCashFlowIndexWithDate(user.rawCashFlow, LocalDate.now());
        const startDate: LocalDate = user.rawCashFlow[offset - 12].startDate;
        const endDate: LocalDate = user.rawCashFlow[offset - 1].endDate;
        // build legend by type
        switch (type) {
            case 'income':
                const incomesDrawerLink: string = `/drawer/incomes/${FormatService.formatSqlDate(
                    startDate
                )}/${FormatService.formatSqlDate(endDate)}`;
                const income: number = CashFlowService.getCashFlowTotalForPeriod(user, 'income');
                titleText = LiteralsService.getLiteral('Timeline.incomeLegendTitle');
                bodyText = LiteralsService.getJsxLiteral('Timeline.incomeLegendBody', [
                    FormatService.format(Math.abs(Number(income))),
                    incomesDrawerLink,
                    '1.3%',
                    incomesDrawerLink
                ]);
                left = this.state.topLegendX;
                top = this.state.topLegendY;
                display = this.state.topLegendOpen;
                break;
            case 'expense':
                const expensesDrawerLink: string = `/drawer/expenses/${FormatService.formatSqlDate(
                    startDate
                )}/${FormatService.formatSqlDate(endDate)}`;
                const expense = CashFlowService.getCashFlowTotalForPeriod(user, 'expense');
                titleText = LiteralsService.getLiteral('Timeline.expenseLegendTitle');
                bodyText = LiteralsService.getJsxLiteral('Timeline.expenseLegendBody', [
                    FormatService.format(Math.abs(Number(expense))),
                    expensesDrawerLink
                ]);
                left = this.state.bottomLegendX;
                top = this.state.bottomLegendY;
                display = this.state.bottomLegendOpen;
                break;
            case 'investment':
                titleText = LiteralsService.getLiteral('Timeline.investmentsLegendTitle');
                bodyText = LiteralsService.getJsxLiteral('Timeline.investmentsLegendBody');
                left = this.state.topLegendX;
                top = this.state.topLegendY;
                display = this.state.topLegendOpen;
                break;
            case 'debt':
                titleText = LiteralsService.getLiteral('Timeline.debtsLegendTitle');
                bodyText = LiteralsService.getJsxLiteral('Timeline.debtsLegendBody');
                left = this.state.bottomLegendX;
                top = this.state.bottomLegendY;
                display = this.state.bottomLegendOpen;
                break;
            case 'networth':
                titleText = LiteralsService.getLiteral('Timeline.netWorthLegendTitle');
                bodyText = LiteralsService.getJsxLiteral('Timeline.netWorthLegendBody');
                left = this.state.topLegendX;
                top = this.state.topLegendY;
                display = this.state.topLegendOpen;
                break;
        }

        return (
            <div
                className={styles.legendContainer}
                style={{
                    display: display ? 'flex' : 'none',
                    left: left - 20,
                    top
                }}
            >
                <Typography variant={TypographyVariants.HEADING_XS} className={styles.legendTitle}>
                    {titleText}
                </Typography>
                <Typography variant={TypographyVariants.SUBTEXT} className={styles.legendBody}>
                    {bodyText}
                </Typography>
                <Anchor target="_blank" rel="noopener noreferrer" href="#">
                    {LiteralsService.getJsxLiteral('Timeline.learnMore')} {<GlyphLinkOut />}
                </Anchor>
                <div className={styles.arrowBottom} />
            </div>
        );
    }

    private showWork(cashFlow: CashFlowModel, type: string, x: number, y: number, value: number, zoom: string): void {
        this.setState({
            showWorkOpen: true,
            showWorkCashFlow: cashFlow,
            showWorkType: type,
            showWorkX: x - 100,
            showWorkY: y - 140,
            showWorkValue: value,
            showWorkZoom: zoom
        });
        if (this.showWorkTimer) {
            clearTimeout(this.showWorkTimer);
        }
        this.showWorkTimer = window.setTimeout(() => {
            this.setState({
                showWorkOpen: false
            });
        }, 5000);
    }

    private endShowWork(): void {
        if (this.showWorkTimer) {
            clearTimeout(this.showWorkTimer);
        }
        this.showWorkTimer = window.setTimeout(() => {
            this.setState({
                showWorkOpen: false
            });
        }, 100);
    }

    private handleOpenDanger(cashFlow: CashFlowModel): void {
        this.setState({
            dangerOpen: true,
            dangerWarn: cashFlow.danger === 1,
            dangerDate: cashFlow.startDate
        });
        if (this.dangerOpenTimer) {
            clearTimeout(this.dangerOpenTimer);
        }
        this.dangerOpenTimer = window.setTimeout(() => {
            this.setState({
                dangerOpen: false
            });
        }, 5000);
    }

    private handleCloseDanger(): void {
        if (this.dangerOpenTimer) {
            clearTimeout(this.dangerOpenTimer);
        }
        this.setState({
            dangerOpen: false
        });
    }

    private resetDimensions(isVisible: boolean = true): void {
        const width: number = isVisible ? UnitService.getViewportWidth() - UnitService.getDrawerWidth() : 1;
        const height: number = UnitService.getViewportHeight();

        const minMagnification: number = (width - UnitService.SCROLL_LEFT_MARGIN) / this.props.user.rawCashFlow.length;
        let magnification: number = minMagnification;

        if (this.props.context) {
            // took out code to make magnification >= 1 APP-6862
            magnification = this.props.context.magnification;
        }
        magnification = Math.max(magnification, minMagnification);

        const monthsFromStart: number = this.props.user.rawCashFlow[0].startDate.until(
            this.props.user.rawCashFlow[this.props.user.rawCashFlow.length - 1].endDate,
            ChronoUnit.MONTHS
        );
        const maxX = magnification * monthsFromStart + UnitService.JAEGER_MARGIN_LEFT;
        const minOffset = Math.min(0, width - maxX);
        const offset: number = Math.max(this.props.context ? this.props.context.offset : 0, minOffset);

        const context: JaegerContext = {
            offset,
            magnification,
            width,
            height,
            timelinePosition: this.props.context ? this.props.context.timelinePosition : TIMELINE_SCREEN_PORTION,
            minMagnification,
            minOffset
        };

        this.props.updateJaegerContext(context);

        this.setState({
            width,
            height
        });
    }

    private handleToggleShowEventNames(): void {
        this.setState({
            showEventNames: !this.state.showEventNames
        });
    }

    private magnify(direction: number): void {
        const wheelEvent = new WheelEvent('wheel', {
            deltaY: direction === 1 ? -120 : 120,
            deltaX: 0
        });

        document.getElementsByClassName('konvajs-content')[0].dispatchEvent(wheelEvent);
    }
}
